import { DoubleArrow } from '@styled-icons/material/DoubleArrow'
import useNativeSmt from '@swarm/core/hooks/data/useNativeSmt'
import { BasicModalProps } from '@swarm/core/hooks/state/usePopupState'
import { generateExplorerUrl } from '@swarm/core/shared/utils'
import { ExtendedSecurity } from '@swarm/types/normalized-entities'
import Dialog from '@swarm/ui/presentational/Dialog'
import Chip from '@swarm/ui/swarm/Buttons/Chip'
import TokenIcon from '@swarm/ui/swarm/TokenIcon'
import { TransactionResult } from 'contract-proxy-kit'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Flex, Text } from 'rimble-ui'
import styled from 'styled-components/macro'

const DetailsList = styled.ul`
  margin: 0;
  padding-left: 30px;
  margin-left: 0px;
  margin-bottom: 20px;

  li::marker {
    color: ${({ theme }) => theme.colors.primary};
  }
`

const DoubleArrowIcon = styled(DoubleArrow)`
  width: 35px;
  height: 35px;
  margin: 0 20px;
  color: ${({ theme }) => theme.colors.primary};
`

interface StakeSecuritySuccessPopupProps extends BasicModalProps {
  stakedAmount: number
  stakedSecurity: ExtendedSecurity
  tx: TransactionResult
}

const StakeSecuritySuccessPopup = ({
  isOpen,
  onClose,
  stakedAmount,
  stakedSecurity,
  tx,
}: StakeSecuritySuccessPopupProps) => {
  const { t } = useTranslation('staking')
  const { nativeSmt } = useNativeSmt()

  const explorerUrl = useMemo(
    () => tx && generateExplorerUrl({ type: 'tx', hash: tx.hash }),
    [tx],
  )

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      width={['100%', '550px']}
      maxHeight={['90%', '80%']}
      height="auto"
      padding={0}
    >
      <Flex flexDirection="column" alignItems="center" mb={3}>
        <Flex justifyContent="center" alignItems="center" mb={3}>
          <TokenIcon symbol={nativeSmt?.symbol} height="95px" maxWidth="95px" />
          <DoubleArrowIcon />
          <TokenIcon
            symbol={stakedSecurity?.symbol}
            height="95px"
            maxWidth="95px"
          />
        </Flex>

        <Text
          fontSize={4}
          fontWeight={5}
          mb={3}
          color="primary"
          textAlign="center"
        >
          {t('stakeSecuritySuccessPopup.title', {
            amount: stakedAmount,
            symbol: stakedSecurity.symbol,
          })}
        </Text>

        <Chip.Link href={explorerUrl} target="_blank" mb={3}>
          {t('stakeSecuritySuccessPopup.viewTx')}
        </Chip.Link>

        <Text fontSize="18px" fontWeight={4}>
          {t('stakeSecuritySuccessPopup.nextStep')}
        </Text>
      </Flex>

      <DetailsList>
        <li>
          <Text fontSize={2} color="primary">
            {t('stakeSecuritySuccessPopup.details.earningsStart', {
              symbol: stakedSecurity.symbol,
            })}
          </Text>
        </li>
        <li>
          <Text fontSize={2} color="primary">
            {t('stakeSecuritySuccessPopup.details.rewardsDistributed')}
          </Text>
        </li>
        <li>
          <Text fontSize={2} color="primary">
            {t('stakeSecuritySuccessPopup.details.claimRewards')}
          </Text>
        </li>
      </DetailsList>

      <Button.Outline
        mx="auto"
        width="auto"
        height="36px"
        px="94px"
        py="6px"
        borderColor="primary"
        border="1.5px solid"
        fontWeight={4}
        onClick={onClose}
      >
        {t('stakingDetails.gotIt')}
      </Button.Outline>
    </Dialog>
  )
}

export default StakeSecuritySuccessPopup
